import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PersonaService } from '../services/persona.service';
import { Persona } from '../models/personas.interface';
import { Strings } from '../shared/strings';
import { ReviewerInfoDialogComponent } from './reviewer-info-dialog/reviewer-info-dialog';
import { ReviewSession } from '../models/review.interface';
import { ReviewService } from '../services/review.service';
import { map, Observable, startWith } from 'rxjs';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Constants } from '../shared/constants';
import {
  ReviewersFilterDialogComponent,
  ReviewersFilterDialogData
} from './filter-dialog/reviewers-filter-dialog.component';
import { CognitoService } from '../services/cognito.service';

@Component({
  selector: 'app-reviewers',
  templateUrl: './reviewers.component.html',
  styleUrls: ['./reviewers.component.scss']
})
export class ReviewersComponent implements OnInit, OnDestroy, AfterViewInit {
  personas!: Persona[];
  cols = 3;
  strings = Strings;
  uploadDialog = inject(MatDialog);
  @ViewChild('personasGrid') personasGrid!: ElementRef;
  selectedPersona!: Persona;
  recentlyReviewed!: ReviewSession[];
  filteredRecentlyReviewed!: ReviewSession[];
  personaFilteredOptions!: Observable<Persona[]>;
  searchFormController = new FormControl('');
  currentFilter: ReviewersFilterDialogData = {
    status: 'ALL',
    myDocuments: false
  };
  filterCount = 0;
  private currentUserId: string | null = null;
  failedTooltipText =
    'The review didn’t complete.\n Try re-uploading.\n If the file is password-protected, remove the password and re-upload.\n If this continues, contact an admin.';

  constructor(
    private personaService: PersonaService,
    private snackBar: MatSnackBar,
    private reviewerService: ReviewService,
    private cognitoService: CognitoService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.getPersonas();
    this.getRecentlyReviewed();
    this.personaFilteredOptions = this.searchFormController.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );

    // Initialize filtered results
    this.filteredRecentlyReviewed = [];

    // Get current user ID
    this.cognitoService.getUserId().then(userId => {
      this.currentUserId = userId;
    });
  }

  ngOnDestroy() {
    if (this.snackBar._openedSnackBarRef) {
      this.snackBar.dismiss();
    }
  }

  ngAfterViewInit(): void {
    // Use setTimeout to ensure the view is fully rendered and has accurate dimensions
    this.setCols();
  }

  private _filter(value: string): Persona[] {
    const filterValue = value.toLowerCase();
    return this.personas.filter(persona => persona.name.toLowerCase().includes(filterValue));
  }

  getPersonas() {
    this.personaService.getPersonas().subscribe({
      next: data => {
        this.personas = data ?? [];
        this.personas.sort((a, b) => a.name.localeCompare(b.name));
      },
      error: error => {
        console.error('There was an error while fetching the personas: ', error);
        if (!this.snackBar._openedSnackBarRef) {
          const ref = this.snackBar.open(Constants.ERROR_MSG_SERVICE_UNAVALIABLE, 'Retry');
          ref.afterDismissed().subscribe(event => {
            if (event.dismissedByAction) {
              window.location.reload();
            }
          });
        }
      }
    });
  }

  showUploadModule(persona: Persona) {
    this.selectedPersona = persona;
    this.uploadDialog.open(ReviewerInfoDialogComponent, {
      width: '600px',
      data: persona
    });
  }

  findPersonaName(personaId: string): string {
    return this.personas.find(p => p.id === personaId)?.name ?? 'Unknown';
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString();
  }

  getRecentlyReviewed() {
    this.reviewerService.getRecentlyReviewed().subscribe({
      next: data => {
        this.recentlyReviewed = data ?? [];
        this.recentlyReviewed.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        this.applyFilters();
      },
      error: error => {
        console.error('There was an error while fetching the recently reviewed documents: ', error);
        if (!this.snackBar._openedSnackBarRef) {
          const ref = this.snackBar.open(Constants.ERROR_MSG_SERVICE_UNAVALIABLE, 'Retry');
          ref.afterDismissed().subscribe(() => {
            window.location.reload();
          });
        }
      }
    });
  }

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(ReviewersFilterDialogComponent, {
      width: '400px',
      data: this.currentFilter
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.currentFilter = result;
        this.applyFilters();
        this.updateFilterCount();
      }
    });
  }

  updateFilterCount(): void {
    let count = 0;
    if (this.currentFilter.status && this.currentFilter.status !== 'ALL') count++;
    if (this.currentFilter.startDate) count++;
    if (this.currentFilter.endDate) count++;
    if (this.currentFilter.myDocuments) count++;
    this.filterCount = count;
  }

  applyFilters(): void {
    if (!this.recentlyReviewed) {
      this.filteredRecentlyReviewed = [];
      return;
    }

    let filtered = [...this.recentlyReviewed];

    // Filter by status
    if (this.currentFilter.status && this.currentFilter.status !== 'ALL') {
      filtered = filtered.filter(doc => doc.status === this.currentFilter.status);
    }

    // Filter by date range
    if (this.currentFilter.startDate) {
      const startDate = new Date(this.currentFilter.startDate);
      filtered = filtered.filter(doc => new Date(doc.created_at) >= startDate);
    }

    if (this.currentFilter.endDate) {
      const endDate = new Date(this.currentFilter.endDate);
      // Set time to end of day
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(doc => new Date(doc.created_at) <= endDate);
    }

    // Filter by current user (My Documents)
    if (this.currentFilter.myDocuments && this.currentUserId) {
      filtered = filtered.filter(doc => doc.created_by === this.currentUserId);
    }

    this.filteredRecentlyReviewed = filtered;
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setCols();
  }

  setCols() {
    if (!this.personasGrid || !this.personasGrid.nativeElement) {
      // If the grid container hasn't been rendered yet, wait a bit and try again
      setTimeout(() => {
        this.setCols();
      }, 20);
      return;
    }

    const containerWidth = this.personasGrid.nativeElement.offsetWidth;
    const itemMinWidth = 230; // Minimum width for each column

    // Calculate how many columns can fit, with a minimum of 1 and maximum of 6
    const calculatedCols = Math.floor(containerWidth / itemMinWidth);
    const newCols = Math.max(1, Math.min(calculatedCols, 6));

    if (newCols !== this.cols) {
      this.cols = newCols;
    }
  }
}
